<template>
  <CustomModal
    :title="isEditMode ? 'Update Service' : 'Create Service'"
    width="34vw"
    v-model:visible="internalVisible"
  >
    <a-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      layout="vertical"
      :id="`${source}_add_service_section`"
      :required-mark="false"
      @finish="onFinish"
    >
      <a-form-item label="Name" name="name" class="mb-3">
        <a-input
          ref="nameInputRef"
          v-model:value="formData.name"
          class="input_medium"
          placeholder="e.g. My Backend"
          :autofocus="true"
          @input="autoGenerateSlug"
        />
      </a-form-item>

      <a-form-item label="Slug" name="slug" class="mb-0">
        <a-input
          v-model:value="formData.slug"
          class="input_medium"
          placeholder="e.g. my-backend"
          @input="slugManuallyEdited = true"
        />
      </a-form-item>
    </a-form>

    <template #footer>
      <div class="flex flex-row items-center justify-end gap-2 w-full">
        <a-button
          class="secondary_medium"
          :disabled="creating"
          @click="handleCancel"
        >
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          html-type="submit"
          :form="`${source}_add_service_section`"
          :loading="creating"
          :disabled="isSubmitDisabled"
        >
          {{
            creating
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update"
                : "Create"
          }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, nextTick } from "vue";
import { message } from "ant-design-vue";
import { errorTrackingEvents } from "@/src/constants/errorTrackingConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import ErrorTrackingService from "@/src/api/ErrorTrackingService.js";
import CustomModal from "@/src/components/CustomModal.vue";

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
  space: { type: Object, default: null },
  service: { type: Object, default: null },
  mode: { type: String, default: "create" },
});

const emit = defineEmits(["update:visible"]);

const creating = ref(false);
const internalVisible = ref(props.visible);
const formRef = ref(null);
const nameInputRef = ref(null);
const slugManuallyEdited = ref(false);

const formData = reactive({
  name: "",
  slug: "",
});

const rules = {
  name: [{ required: true, message: "Please enter service name", trigger: "change" }],
  slug: [{ required: true, message: "Please enter a slug", trigger: "change" }],
};

const isEditMode = computed(() => props.mode === "edit");

const isSubmitDisabled = computed(
  () => !formData.name || !formData.slug || formData.name.trim() === "" || creating.value,
);

const autoGenerateSlug = () => {
  if (!slugManuallyEdited.value) {
    formData.slug = formData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }
};

const resetForm = () => {
  try {
    Object.assign(formData, { name: "", slug: "" });
    slugManuallyEdited.value = false;
    creating.value = false;
    formRef.value?.clearValidate();
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const handleCancel = () => {
  try {
    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while closing modal: ", error);
  }
};

const onFinish = async () => {
  if (isEditMode.value) {
    await toUpdateService();
  } else {
    await toCreateService();
  }
};

const toCreateService = async () => {
  try {
    creating.value = true;
    const payload = {
      name: formData.name,
      slug: formData.slug,
      space_id: props.space?.id,
    };
    const response = await ErrorTrackingService.createService(payload);
    const service = response?.data || response;
    if (service) {
      message.success(`Service ${formData.name} created successfully!`);
      const eventData = {
        event: errorTrackingEvents.CREATED,
        item: service,
        spaceId: props.space?.id,
      };
      internalVisible.value = false;
      resetForm();
      eventBus.emit(errorTrackingEvents.SERVICE_MANIPULATED, eventData);
    }
  } catch (error) {
    console.error("An error occurred while creating service: ", error);
    message.error("An error occurred while creating a service!");
  } finally {
    creating.value = false;
  }
};

const toUpdateService = async () => {
  try {
    creating.value = true;
    const payload = { name: formData.name, slug: formData.slug };
    const response = await ErrorTrackingService.updateService(props.service?.id, payload);
    if (response) {
      message.success("Service updated successfully!");
      internalVisible.value = false;
      resetForm();
      eventBus.emit(errorTrackingEvents.SERVICE_MANIPULATED, { event: errorTrackingEvents.UPDATED });
    }
  } catch (error) {
    console.error("An error occurred while updating service: ", error);
    message.error("An error occurred while updating service!");
  } finally {
    creating.value = false;
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      await nextTick();
      nameInputRef.value?.focus();
    }
  },
  { immediate: true },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
    if (!newVal) resetForm();
  },
);

onMounted(() => {
  if (isEditMode.value && props.service) {
    formData.name = props.service.name || "";
    formData.slug = props.service.slug || "";
    slugManuallyEdited.value = true;
    formRef.value?.clearValidate();
  }
});

watch(
  () => [props.service, props.visible, props.mode],
  ([service, visible, mode]) => {
    if (visible && mode === "edit" && service) {
      formData.name = service.name || "";
      formData.slug = service.slug || "";
      slugManuallyEdited.value = true;
      formRef.value?.clearValidate();
    }
  },
  { immediate: true },
);
</script>
